<template>
  <div class="fair-survey-step fair-survey-step--wide">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="fair-survey-step__grid">
      <button
        v-for="style in step.selectedStyles"
        :key="style.styleId"
        type="button"
        class="fair-survey-step__card"
        :class="{
          'fair-survey-step__card--selected': isStyleSelected(style.styleId),
        }"
        :aria-pressed="isStyleSelected(style.styleId)"
        @click="toggleStyle(style.styleId)"
      >
        <span class="style-evaluation-step__media">
          <img
            class="fair-survey-step__card-media"
            :src="getStyleImage(style)"
            :alt="style.name"
          >
          <span
            v-if="style.isCompleted"
            class="style-evaluation-step__done"
          >
            <FairIcon
              name="check"
              :size="12"
            />
          </span>
        </span>

        <span class="fair-survey-step__card-label">{{ style.name }}</span>

        <span class="style-evaluation-step__bar">
          <span
            class="style-evaluation-step__bar-fill"
            :style="{ width: `${style.progressPercent}%` }"
          />
        </span>

        <span class="fair-survey-step__card-meta">
          {{ style.progressPercent }}% · {{ style.swipedCards }}/{{ step.swipesForAccuracy }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'
import { mapStyleImageFromBackend } from '@/utils/style-image-mapper'

interface Props {
  step: TasteStep & { type: 'style_evaluation' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedStyleIds = computed(() => {
  const data = tasteStore.tasteModal.tasteData[props.step.id]
  if (!data) return []
  
  // Если это массив, возвращаем как есть (нормализуем к числам)
  if (Array.isArray(data)) {
    return data.map((id: any) => typeof id === 'number' ? id : parseInt(id, 10)).filter((id: any) => !isNaN(id))
  }
  
  // Если это одно значение (старый формат), преобразуем в массив
  if (typeof data === 'string' || typeof data === 'number') {
    const id = typeof data === 'number' ? data : parseInt(data, 10)
    return isNaN(id) ? [] : [id]
  }
  
  return []
})

function isStyleSelected(styleId: number): boolean {
  return selectedStyleIds.value.includes(styleId)
}

function toggleStyle(styleId: number | string) {
  const styleIdNumber = typeof styleId === 'number' ? styleId : parseInt(styleId, 10)
  if (isNaN(styleIdNumber)) return
  
  const currentIds = [...selectedStyleIds.value]
  const index = currentIds.indexOf(styleIdNumber)
  
  if (index > -1) {
    // Удаляем стиль из выбранных
    currentIds.splice(index, 1)
  } else {
    // Добавляем стиль к выбранным
    currentIds.push(styleIdNumber)
  }
  
  // Сохраняем обновленный список выбранных стилей
  tasteStore.updateTasteData(props.step.id, currentIds)
  
  // Сохраняем выбранные стили для использования в tinder_like шаге
  // Используем ключ 'style-evaluation-choice' для обратной совместимости
  tasteStore.updateTasteData('style-evaluation-choice', currentIds.map(id => id.toString()))
}

function getStyleImage(style: { code?: string; image?: string }): string {
  return mapStyleImageFromBackend(style)
}

// Автоматически выбираем стили из style-selection-step при монтировании
onMounted(() => {
  // Находим шаг style_selection в конфигурации
  const styleSelectionStep = tasteStore.steps.find(step => step.type === 'style_selection')
  
  if (!styleSelectionStep) return
  
  // Получаем выбранные стили из tasteData для шага style_selection
  const selectedStylesFromSelection = tasteStore.tasteModal.tasteData[styleSelectionStep.id]
  
  if (!selectedStylesFromSelection || !Array.isArray(selectedStylesFromSelection)) return
  
  // Нормализуем ID к числам
  const selectedStyleIdsFromSelection = selectedStylesFromSelection
    .map((id: any) => typeof id === 'number' ? id : parseInt(id, 10))
    .filter((id: any) => !isNaN(id))
  
  if (selectedStyleIdsFromSelection.length === 0) return
  
  // Находим стили из step.selectedStyles, которые соответствуют выбранным на style-selection-step
  const stylesToSelect = props.step.selectedStyles
    .filter(style => selectedStyleIdsFromSelection.includes(style.styleId))
    .map(style => style.styleId)
  
  if (stylesToSelect.length === 0) return
  
  // Проверяем, есть ли уже сохраненные данные для текущего шага
  const currentData = tasteStore.tasteModal.tasteData[props.step.id]
  
  // Если данных еще нет, автоматически выбираем все найденные стили
  if (!currentData || (Array.isArray(currentData) && currentData.length === 0)) {
    tasteStore.updateTasteData(props.step.id, stylesToSelect)
    tasteStore.updateTasteData('style-evaluation-choice', stylesToSelect.map(id => id.toString()))
  }
})
</script>

<style scoped>
.style-evaluation-step__media {
  position: relative;
  display: block;
}

.style-evaluation-step__done {
  position: absolute;
  right: var(--space-4);
  bottom: var(--space-4);
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background: var(--color-signal-positive-text);
  color: var(--color-text-on-dark);
}

.style-evaluation-step__bar {
  display: block;
  height: 2px;
  overflow: hidden;
  border-radius: var(--radius-full);
  background: var(--color-fill-chip);
}

.style-evaluation-step__bar-fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-full);
  background: var(--color-primary);
}
</style>
